import { Injectable, OnDestroy, inject, DestroyRef } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Subject, Observable, from, EMPTY, of } from 'rxjs';
import { distinctUntilChanged, switchMap, tap, catchError, filter } from 'rxjs/operators';
import { AuthService } from '../components/framework/services/auth.service';
import { environment } from '../../../environments/environment';
import { IUserNotification } from '../stores';

@Injectable({
    providedIn: 'root'
})
export class SignalRService implements OnDestroy {
    private readonly signalRConnection: string;
    private readonly destroyRef = inject(DestroyRef);
    private hubConnection: signalR.HubConnection | undefined;
    private isInitialized = false;

    // Connection state management
    private readonly connectionState = new BehaviorSubject<signalR.HubConnectionState>(
        signalR.HubConnectionState.Disconnected
    );

    // Public observables for real-time events
    public readonly taskCreated$ = new BehaviorSubject<any>(null);
    public readonly taskChanged$ = new BehaviorSubject<any>(null);
    public readonly taskDeleted$ = new BehaviorSubject<any>(null);
    public readonly unreadCountsUpdated$ = new BehaviorSubject<any>(null);

    // Public observable for notifications
    public readonly notificationReceived$ = new Subject<any>();

    constructor(
        private readonly authService: AuthService,
    ) {
        // Derive SignalR hub URL from taskManagerServiceEndPoint (per copilot-instructions.md)
        this.signalRConnection = environment.taskManagerServiceEndPoint
            .replace('/taskmanagerservice/api', '')
            .replace('/api', '') + '/notificationhub';
    }

    /**
     * Initialize SignalR service - call this from APP_INITIALIZER or after auth is ready
     * Subscribes to auth changes and handles initial connection if authenticated
     */
    public initialize(): void {
        if (this.isInitialized) return;
        this.isInitialized = true;

        // Set up reactive auth subscription - auto-cleanup via takeUntilDestroyed
        this.authService.loginChanged.pipe(
            distinctUntilChanged(),
            switchMap(isLoggedIn => {
                if (isLoggedIn) {
                    return this.startConnection().pipe(
                        catchError(err => {
                            console.warn('⚠️ SignalR: Failed to start on login:', err);
                            return EMPTY; // Continue without throwing to avoid breaking the app if connection fails
                        })
                    );
                } else {
                    return this.stopConnection();
                }
            }),
            takeUntilDestroyed(this.destroyRef) // ✅ Auto-cleanup on service destroy
        ).subscribe();

        // Handle initial authentication state (page reload)
        from(this.authService.isAuthenticatedAsync()).pipe(
            filter(isAuth => isAuth),
            switchMap(() => this.startConnection()),
            catchError(err => {
                console.warn('⚠️ SignalR: Failed to start on init:', err);
                return EMPTY;
            }),
            takeUntilDestroyed(this.destroyRef) // ✅ Auto-cleanup on service destroy
        ).subscribe();
    }

    /**
     * Start SignalR connection - returns Observable for reactive composition
     */
    private startConnection(): Observable<void> {
        // Prevent multiple simultaneous starts
        if (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
            // console.log('🔌 SignalR: Already connected or connecting');
            return of(void 0);
        }

        return from(this.createAndStartConnection()).pipe(
            tap(() => this.connectionState.next(this.hubConnection?.state ?? signalR.HubConnectionState.Disconnected)),
            catchError(err => this.handleConnectionError(err))
        );
    }

    /**
     * Create and start SignalR connection with dynamic token factory
     * Uses OIDC token (preferred) or falls back to localStorage token
     */
    private async createAndStartConnection(): Promise<void> {
        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(this.signalRConnection, {
                accessTokenFactory: async () => {
                    // Token pattern: prefer OIDC, fallback to localStorage (per copilot-instructions.md)
                    const accessToken = await this.authService.getAccessToken();
                    const finalToken = accessToken ? `Bearer ${accessToken}` : localStorage.getItem("tasksApiToken");
                    return finalToken?.replace('Bearer ', '') ?? '';
                },
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect([0, 2000, 10000, 30000])
            .configureLogging(signalR.LogLevel.Information)
            .build();

        this.setupEventListeners();

        await this.hubConnection.start();
        // console.log('🔌 SignalR: Connected to hub');
    }

    /**
     * Set up event listeners for SignalR hub events
     * Real-time events: taskCreated$, taskChanged$, taskDeleted$ (per copilot-instructions.md)
     */
    private setupEventListeners(): void {
        if (!this.hubConnection) return;

        // Task lifecycle events
        this.hubConnection.on('TaskCreated', (data) => {
            // console.log('📥 SignalR: TaskCreated', data);
            this.taskCreated$.next(data);
        });

        this.hubConnection.on('TaskChanged', (data) => {
            // console.log('📥 SignalR: TaskChanged', data);
            this.taskChanged$.next(data);
        });

        this.hubConnection.on('TaskDeleted', (data) => {
            // console.log('📥 SignalR: TaskDeleted', data);
            this.taskDeleted$.next(data);
        });

        this.hubConnection.on('UnreadCountsUpdated', (data) => {
            // console.log('📥 SignalR: UnreadCountsUpdated', data);
            this.unreadCountsUpdated$.next(data);
        });

        // Notification events
        this.hubConnection.on('NotificationUpdate', (data: IUserNotification) => {
            // console.log('📥 SignalR: NotificationUpdate', data);
            this.notificationReceived$.next(data);
        });

        // Connection lifecycle events
        this.hubConnection.onreconnecting(() => {
            // console.log('🔄 SignalR: Reconnecting...');
            this.connectionState.next(signalR.HubConnectionState.Reconnecting);
        });

        this.hubConnection.onreconnected(() => {
            // console.log('✅ SignalR: Reconnected');
            this.connectionState.next(signalR.HubConnectionState.Connected);
        });

        this.hubConnection.onclose((error) => {
            // console.log('❌ SignalR: Connection closed', error);
            this.connectionState.next(signalR.HubConnectionState.Disconnected);
        });
    }

    /**
     * Stop SignalR connection - returns Observable for reactive composition
     */
    private stopConnection(): Observable<void> {
        if (!this.hubConnection || this.hubConnection.state === signalR.HubConnectionState.Disconnected) {
            return of(void 0);
        }

        return from(this.hubConnection.stop()).pipe(
            tap(() => {
                // console.log('🔌 SignalR: Disconnected');
                this.connectionState.next(signalR.HubConnectionState.Disconnected);
            }),
            catchError(err => {
                console.error('❌ SignalR: Error stopping connection', err);
                return EMPTY;
            })
        );
    }

    /**
     * Handle connection errors - return empty observable to continue gracefully
     */
    private handleConnectionError(err: any): Observable<void> {
        console.error('❌ SignalR: Connection error', err);
        this.connectionState.next(signalR.HubConnectionState.Disconnected);

        return EMPTY; // Continue without throwing
    }

    ngOnDestroy(): void {
        // Subscriptions are automatically cleaned up via takeUntilDestroyed
        if (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
            this.hubConnection.stop().catch((err) => {
                console.error('❌ SignalR: Error stopping connection on destroy', err);
            });
        }
    }
}